"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Hours: ONE parser and ONE formatter. CLAUDE.md §3 says so, in one line.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Hours parse from `8`, `8h`, `8:00`, `8,0` and render `H:MM`, in one function."*
 *
 * ⚠️ **It lives here rather than in `apps/api`, and it is needed by C8 rather than C11.** The
 * worklog chunk owns the *surfaces* that log time; the task estimate field needs the same parsing
 * on the commit that introduces it, and a second copy is how a screen comes to accept `8,5` while
 * the server stores `8`. The shared-package rule already covers this: *a pure function over a type
 * declared here belongs here, even if only one caller exists today.*
 *
 * ⚠️ **`8,0` is not a typo in the spec — Hungarian writes a decimal comma.** Anybody typing hours
 * on a Hungarian keyboard produces it by default, and a parser that rejects it is a parser that
 * rejects the majority of its input. `8.5` works too, because both appear in real data.
 *
 * ⚠️ **`8:30` is NOT 8.30.** The colon form is hours and minutes, so it is 8.5 — and this is the
 * one mistake that produces plausible wrong numbers rather than an error, which is why the two
 * forms cannot share a code path.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseHours = parseHours;
exports.formatHours = formatHours;
/** Two decimal places, matching `numeric(8,2)`. Anything finer is not storable anyway. */
const PRECISION = 100;
/**
 * Parse a typed duration into hours, or `null` when it is not one.
 *
 * ⚠️ **`null`, not `0`.** The mockup's `parseHours` returns `NaN` and its callers write
 * `parseHours(v) || 0`, which turns *"I typed something wrong"* into *"zero hours"* — a silent
 * data loss dressed as a successful save. A caller here has to decide what an unparseable value
 * means, and the answer is almost always "refuse it".
 */
function parseHours(input) {
    if (typeof input === 'number') {
        return Number.isFinite(input) && input >= 0 ? round(input) : null;
    }
    const text = input.trim().toLowerCase();
    if (text === '')
        return null;
    /* ── `8:30`, `8:5` — hours and minutes ────────────────────────────────────────────────── */
    const colon = /^(\d{1,4}):([0-5]?\d)$/.exec(text);
    if (colon !== null) {
        const hours = Number(colon[1]);
        const minutes = Number(colon[2]);
        return round(hours + minutes / 60);
    }
    /* ── `8`, `8h`, `8,5`, `8.5h`, `8 h` ──────────────────────────────────────────────────── */
    const decimal = /^(\d{1,4})(?:[.,](\d{1,4}))?\s*h?$/.exec(text);
    if (decimal !== null) {
        /* ⚠️ Rebuilt with a `.` rather than `Number(text.replace(',', '.'))` on the whole string: that
         * form also accepts `8h5`, because `Number` stops at the `h` in some engines and not others. */
        /* ⚠️ `?? ''` on group 1 as well, and it is not defensive noise: a capture group is typed
         * `string | undefined` even when the pattern makes it unconditional, and lint refuses it in a
         * template. Group 1 cannot actually be absent here — the pattern requires it. */
        const whole = decimal[1] ?? '0';
        const fraction = decimal[2] ?? '';
        return round(Number(fraction === '' ? whole : `${whole}.${fraction}`));
    }
    /* ── `1h30`, `1h 30m`, `90m` — the forms people type without being asked to ───────────── */
    const composite = /^(?:(\d{1,4})\s*h)?\s*(?:(\d{1,4})\s*m)?$/.exec(text);
    if (composite !== null && (composite[1] !== undefined || composite[2] !== undefined)) {
        const hours = Number(composite[1] ?? '0');
        const minutes = Number(composite[2] ?? '0');
        /**
         * ⚠️ **`90m` IS valid; `1h90m` is not** — and the first version refused both, because it
         * capped minutes at 59 unconditionally. Bare minutes are a duration somebody chose to write in
         * minutes: 90 of them is an hour and a half, and refusing it rejects the natural way to record
         * a short task. Once an hour part is present, a minutes part over 59 is a typo rather than an
         * unusual unit — nobody means `2:30` when they type `1h90m`.
         */
        if (composite[1] !== undefined && minutes > 59)
            return null;
        return round(hours + minutes / 60);
    }
    /* ⚠️ `1h30`, with no trailing `m` — not covered above, because that pattern requires the `m`.
     * Capped at 59 here for the reason the composite branch gives: the hour part is present. */
    const hAndDigits = /^(\d{1,4})\s*h\s*([0-5]?\d)$/.exec(text);
    if (hAndDigits !== null) {
        return round(Number(hAndDigits[1]) + Number(hAndDigits[2]) / 60);
    }
    return null;
}
/**
 * Render hours as `H:MM`.
 *
 * ⚠️ **`H:MM`, so `7.5` reads `7:30`.** Rendering `7.5` somewhere and `7:30` elsewhere is how one
 * screen disagrees with the next about the same number, which reads as a data problem.
 *
 * ⚠️ **`0:00` for zero, not an em-dash.** Zero hours is a computed answer; the em-dash rule is for
 * things that *cannot* be computed. A caller wanting the dash for "nothing logged yet" decides that
 * itself, because only the caller knows the difference.
 */
function formatHours(hours) {
    const value = typeof hours === 'string' ? Number(hours) : hours;
    if (!Number.isFinite(value))
        return '0:00';
    const sign = value < 0 ? '-' : '';
    const total = Math.round(Math.abs(value) * 60);
    const whole = Math.floor(total / 60);
    const minutes = total % 60;
    return `${sign}${String(whole)}:${String(minutes).padStart(2, '0')}`;
}
function round(value) {
    return Math.round(value * PRECISION) / PRECISION;
}
//# sourceMappingURL=hours.js.map